import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TableHeaderWithArrow from './TableHeaderWithArrow'
import styles from './styles/search-results-table-desktop.module.scss'
import SearchResult from './SearchResultDesktop'
import { Player, Sorting, SortingField } from 'types/global'

interface Props {
  players?: Player[]
  sorting: Sorting | null
  handleSelectSorting: (field: SortingField) => void
  renderTableRow: (player: Player) => ReturnType<typeof SearchResult>
  classHiddenCell?: string
  /**
   * Setting this to `true` makes sure the header's cells have the same width as the body's (i.e. in case the table head is fixed and the table body is scrollable)
   */
  resizeHeadersRow?: boolean
}

const SearchResultsTableDesktop: React.FC<Props> = ({
  players,
  sorting,
  handleSelectSorting,
  renderTableRow,
  classHiddenCell,
  resizeHeadersRow = false,
}) => {
  const { t: tSearchResults } = useTranslation('searchResults')
  const [rowWidth, setRowWidth] = useState<number>()
  const bodyRef = useRef<HTMLTableSectionElement>(null)

  useEffect(() => {
    function resizeTopRow() {
      if (bodyRef.current) {
        setRowWidth(bodyRef.current.firstElementChild?.clientWidth)
      }
    }

    if (resizeHeadersRow) {
      resizeTopRow()
      window.addEventListener('resize', resizeTopRow)
    }

    return () => {
      if (resizeHeadersRow) {
        window.removeEventListener('resize', resizeTopRow)
      }
    }
  }, [resizeHeadersRow])

  return (
    <table className={styles.searchPlayersResultsTable}>
      <thead className={styles.resultsHeader}>
        <tr style={rowWidth ? { width: `${rowWidth}px` } : undefined}>
          <TableHeaderWithArrow
            isActive={sorting?.field === 'nativeFamilyName'}
            fieldToUpdate={SortingField.FamilyName}
            onClick={handleSelectSorting}
            direction={sorting?.direction}
            label={tSearchResults('playersHeading')}
          />
          <th scope='col'>{tSearchResults('nationalityHeading')}</th>
          <th scope='col'>{tSearchResults('ageGroupHeading')}</th>
          <TableHeaderWithArrow
            isActive={sorting?.field === 'ratings.ITF.scaledRating'}
            fieldToUpdate={SortingField.WTNSingles}
            onClick={handleSelectSorting}
            direction={sorting?.direction}
            label={tSearchResults('singlesHeading')}
            classNames={{ label: styles.labelWtn }}
          />
          <TableHeaderWithArrow
            isActive={sorting?.field === 'ratings.ITFDBL.scaledRating'}
            fieldToUpdate={SortingField.WTNDoubles}
            onClick={handleSelectSorting}
            direction={sorting?.direction}
            label={tSearchResults('doublesHeading')}
            classNames={{ label: styles.labelWtn }}
          />
          <th scope='col' aria-hidden className={classHiddenCell}></th>
        </tr>
      </thead>
      <tbody ref={bodyRef}>{players && players.map(renderTableRow)}</tbody>
    </table>
  )
}

export default SearchResultsTableDesktop
